import { Injectable } from '@angular/core';
import { TravelType } from '@app/enums/travel-type.enum';
import { SingleFlightFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/flight-request.formgroup';
import { LodgingRequestDetailsFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/lodging-request.formgroup';
import { RentalCarRequestDetailsFormGroup } from '@components/request/travel-request-card/form-groups/detail-form-groups/travel/rental-car-request.formgroup';
import { MomentFormat } from '@models/date-format';
import { CreateGetThereUrlViewModel } from '@models/getthere/createGetThereUrlViewModel';
import { CabinClass, GetThereInputParameters, PricingType } from '@models/getthere/getThereInputParameters';
import { GetThereInputParametersDTO } from '@models/getthere/getThereInputParametersDTO';
import { VisitDetails } from '@models/request';
import { GetThereRepository } from '@repositories/getthere/getthere.repository';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { TimeOptionsStateService } from '@services/state-management/time-options-state.service';
import { mapToGetThereCountryCode, mapToGetThereCountryName } from '@services/transforms/getThere-transform';
import { FormGroupValueOf } from '@utility/utility';
import { utc } from 'moment';
import { Observable } from 'rxjs';

/** An object representation of search pre-population parameters accepted by GetThere. The exact names of parameter in this class might differ from what we pass to GetThere, as there is a mapping of types before we send them. */

export function mapToDTO(value: GetThereInputParameters): GetThereInputParametersDTO {
    let dto: GetThereInputParametersDTO = {
        ...value,
        'departList[0]': value.origin,
        'destinationList[0]': value.destination,
        'monAbbrList[0]': value.departureDate ? (value.departureDate.month() + 1).toString() : '',
        'monAbbrList[1]': value.returnDate ? (value.returnDate.month() + 1).toString() : '',
        'dateList[0]': value.departureDate ? value.departureDate.date().toString() : '',
        'dateList[1]': value.returnDate ? value.returnDate.date().toString() : '',
        'timeList[0]': value.departureDate ? value.departureDate.hour().toString() : '',
        'timeList[1]': value.returnDate ? value.returnDate.hour().toString() : '',
        'travelMethodList[0]': value.departureTravelMethod,
        'travelMethodList[1]': value.returnTravelMethod,
    };
    for (const key in dto) {
        if (dto[key] === undefined || dto[key] === null || dto[key] === '') dto[key] = '';
    }
    return dto;
}
export interface GetThereTravelerId {
    travelerId: string;
    firstName: string;
    middleName: string;
    lastName: string;
    emailAddress: string;
    country: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipcode: string;
    phoneNumber: string;
    gender: string;
    birthDate: string;
    studyCode: string;
    siteNumber: string;
    patientId: string;
}
@Injectable({
    providedIn: 'root',
})
export class GetThereService {
    constructor(
        private getThereRepository: GetThereRepository,
        private timeOptionsService: TimeOptionsStateService,
        private snackbarService: SnackbarService
    ) {}

    getUrl(inputParameters: GetThereInputParametersDTO): Observable<CreateGetThereUrlViewModel> {
        return this.getThereRepository.getUrl(inputParameters);
    }

    getInputStringForNewBooking(): GetThereInputParameters {
        return {
            user_flag: 'c',
            msg_e: '',
            user: '',

            sub_site: 'medpace_guest',
            ss_key: 'newuser',

            first_name: '',
            middle_i: '',
            last_name: '',
            email: '',
            sfp_birth_day: '',
            sfp_birth_month: '',
            sfp_birth_year: '',
            gender: '',

            origin: '',
            destination: '',
            tripType: 'roundTrip',
            cabinClass: CabinClass.Coach,
            pricingType: PricingType.Unrestricted,
            need_air: 'no',
            need_hotel: 'no',
            need_car: 'no',
            need_rail: 'no',
            numPassengers: 1,

            address: '',
            city: '',
            company: '',
            corp_ID: '',
            cost_ctr: '',
            country: '',
            countryCode: '',
            date_visit: '',
            day_phone: '',
            department: '',
            departureDate: null,
            departureTravelMethod: '',
            division: '',
            emp_no: '',
            err_url: '',
            home_phone: '',
            mail_stop: '',

            /** Business owners decision (#10403): we should always send a constant string `866-715-4403` which is main PCS phone line */
            mobile_phone: '866-715-4403',

            patient_id: '',

            /** Business owner's decision (#10403): we should send a constant string `Patient Recruitment Coordinator` here */
            position: 'Patient Recruitment Coordinator',

            project_code: '',
            returnDate: null,
            returnTravelMethod: '',

            /** Business owners decision (#10403): we should always send a constant string `N/A` */
            sfp_known_traveler_number: 'N/A',

            site_num: '',
            state: '',
            visit_num: '',
            zip: '',
        };
    }

    createGetThereURLParams(
        getThereTravelerId: GetThereTravelerId,
        needsAirTravel: boolean,
        flightDetails: FormGroupValueOf<SingleFlightFormGroup>,
        needsLodging: boolean,
        lodgingDetails: FormGroupValueOf<LodgingRequestDetailsFormGroup>,
        needsCarRental: boolean,
        carRentalDetails: FormGroupValueOf<RentalCarRequestDetailsFormGroup>,
        visitDetails: VisitDetails
    ): GetThereInputParameters {
        if (!getThereTravelerId) {
            throw Error(`Patient information is required to redirect to GetThere, but it was not found`);
        }

        let params = this.getInputStringForNewBooking();
        params.user = getThereTravelerId.travelerId;

        params.need_air = needsAirTravel ? 'yes' : 'no';
        params.need_rail = 'no';
        params.need_hotel = needsLodging ? 'yes' : 'no';
        params.need_car = needsCarRental ? 'yes' : 'no';
        if (needsAirTravel) {
            const travelType = flightDetails.travelType;
            params.tripType =
                travelType === TravelType.ONE_WAY ? 'oneWay' : travelType === TravelType.ROUND_TRIP ? 'roundTrip' : '';
            params.origin = flightDetails.airportOrigin;
            params.destination = flightDetails.airportDestination;
            params.departureDate = flightDetails.departureDate?.set('hour', 12);
            if (travelType === TravelType.ROUND_TRIP) {
                params.returnDate = flightDetails.returnDate?.set('hour', 12);
            }
        } else if (needsLodging) {
            params.departureDate = lodgingDetails.checkInDate?.set('hour', 12);
            params.returnDate = lodgingDetails.checkOutDate?.set('hour', 12);

            // In PCS we don't have a travel location defined in "Lodging" tab (separately from flight destination).
            // So, if we were requested to only book hotel (but not flight), we don't know where that hotel is.
            // For now, we'll just leave field empty and allow users fill the hotel location field manually.
            params.origin = '';
            params.destination = '';
        } else if (needsCarRental) {
            params.departureDate = carRentalDetails.pickupDate
                ?.startOf('day')
                .add(carRentalDetails.dropOffTime || 0, 'minutes');
            params.returnDate = carRentalDetails.dropOffDate
                ?.startOf('day')
                .add(carRentalDetails.dropOffTime || 0, 'minutes');
            params.origin = '';
            params.destination = carRentalDetails.pickupAddress;
            params.countryCode = mapToGetThereCountryCode(carRentalDetails.pickupCountry, this.snackbarService);
        }

        params.visit_num = visitDetails.visitName.name;
        params.date_visit = visitDetails.visitStartDate.format(MomentFormat.dateOnly);
        params.departureTravelMethod = 'departs';
        params.returnTravelMethod = 'departs';
        params.numPassengers = 1; // more than one is disabled

        params.first_name = getThereTravelerId.firstName;
        params.middle_i = getThereTravelerId.middleName;
        params.last_name = getThereTravelerId.lastName;
        params.email = getThereTravelerId.emailAddress;
        params.country = this.mapCountry(getThereTravelerId.country);
        params.address = getThereTravelerId.address1;
        params.mail_stop = getThereTravelerId.address2;
        params.city = getThereTravelerId.city;
        params.state = getThereTravelerId.state;
        params.zip = getThereTravelerId.zipcode;
        params.day_phone = getThereTravelerId.phoneNumber;
        params.home_phone = getThereTravelerId.phoneNumber;
        params.gender = this.mapToGetThereGender(getThereTravelerId.gender);
        params.sfp_birth_day = utc(getThereTravelerId.birthDate).format('D');
        params.sfp_birth_month = utc(getThereTravelerId.birthDate).format('M');
        params.sfp_birth_year = utc(getThereTravelerId.birthDate).format('Y');
        params.project_code = getThereTravelerId.studyCode;
        params.site_num = getThereTravelerId.siteNumber;
        params.patient_id = getThereTravelerId.patientId;
        return params;
    }

    /* Maps country name,
     *  - from: a string representation used by PCS
     *  - to: a full country name string representation used by GetThere [https://wtools.getthere.net/code/CountryCodes.jsp]
     */
    mapCountry(country: string): string {
        return mapToGetThereCountryName(country, this.snackbarService);
    }

    /* Maps country name,
     *  - from: a string representation used by PCS
     *  - to: a 2-letter code string representation used by GetThere [https://wtools.getthere.net/code/CountryCodes.jsp]
     */
    mapCountryToGetThereCountryCode(country: string): string {
        return mapToGetThereCountryCode(country, this.snackbarService);
    }

    mapToGetThereGender(gender: string): '' | 'm' | 'f' {
        if (gender === 'female') return 'f';
        if (gender === 'male') return 'm';
        return '';
    }
}
